/**
 * Drag-and-drop or click-to-pick image upload.
 * Uses presigned POST to S3 — never proxies bytes through our server.
 * Auto-optimizes images: resizes to max 1600px wide and converts to WebP
 * (typically saves 60-80% of bandwidth vs the original JPEG/PNG).
 * Props:
 *   value: current public_url (string)
 *   onChange(public_url): called after successful upload
 *   folder: 'products' | 'farms' | 'drops' | 'recipes'
 *   testId: data-testid prefix
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { UploadCloud, X, ImageOff, Link as LinkIcon } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const MAX_DIMENSION = 1600;
const WEBP_QUALITY = 0.85;

/**
 * Resize + convert to WebP via <canvas>. Returns the converted File.
 * GIFs are passed through (would lose animation if converted).
 */
async function optimizeImage(file) {
  if (file.type === "image/gif") return file;
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = URL.createObjectURL(file);
    });
    let { width, height } = img;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", WEBP_QUALITY));
    URL.revokeObjectURL(img.src);
    if (!blob) return file;
    const newName = file.name.replace(/\.[^.]+$/, "") + ".webp";
    return new File([blob], newName, { type: "image/webp" });
  } catch {
    return file; // fall back to original on any failure
  }
}

export default function ImageUploader({ value, onChange, folder = "products", testId = "img-uploader" }) {
  const [configured, setConfigured] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const [drag, setDrag] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/uploads/status").then(r => setConfigured(!!r.data.configured)).catch(() => setConfigured(false));
  }, []);

  const upload = async (file) => {
    setErr(""); setProgress(0);
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please pick an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Image must be under 5 MB."); return; }
    setBusy(true);
    try {
      // Client-side optimize → WebP @ max 1600px (skipped for GIFs)
      const optimized = await optimizeImage(file);
      const { data } = await api.post("/uploads/presign", {
        folder, content_type: optimized.type, filename: optimized.name,
      });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", data.upload_url);
        const fd = new FormData();
        Object.entries(data.fields || {}).forEach(([k, v]) => fd.append(k, v));
        fd.append("file", optimized);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`S3 upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Network error uploading to S3"));
        xhr.send(fd);
      });
      onChange(data.public_url);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setBusy(false); setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  if (configured === false) {
    // S3 not configured — fall back to manual URL input
    return (
      <input
        type="url"
        placeholder="https://… (paste image URL)"
        className="input-base"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-url-fallback`}
      />
    );
  }

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testId}-file-input`}
      />
      {value ? (
        <div className="relative border border-[var(--ff-border)] rounded-lg overflow-hidden bg-[var(--ff-muted)]">
          <img src={value} alt="" className="w-full max-h-48 object-contain bg-white" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          <div className="absolute top-2 right-2 flex gap-1">
            <button type="button" onClick={() => inputRef.current?.click()} className="bg-white/95 hover:bg-white text-xs px-2 py-1 rounded shadow-sm" disabled={busy} data-testid={`${testId}-replace`}>Replace</button>
            <button type="button" onClick={() => onChange("")} className="bg-white/95 hover:bg-white text-xs px-2 py-1 rounded shadow-sm text-rose-700" disabled={busy} data-testid={`${testId}-remove`} aria-label="Remove"><X size={14}/></button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${drag ? "border-[var(--ff-primary)] bg-emerald-50" : "border-[var(--ff-border)] hover:border-[var(--ff-primary)] bg-white"}`}
          data-testid={`${testId}-dropzone`}
        >
          {busy ? (
            <div>
              <UploadCloud size={24} className="mx-auto text-[var(--ff-primary)] animate-pulse"/>
              <p className="text-sm mt-2">Uploading… {progress}%</p>
              <div className="mt-2 h-1.5 bg-[var(--ff-border)] rounded overflow-hidden max-w-xs mx-auto">
                <div className="h-full bg-[var(--ff-primary)] transition-all" style={{ width: `${progress}%` }}/>
              </div>
            </div>
          ) : (
            <>
              <UploadCloud size={24} className="mx-auto text-[var(--ff-text-muted)]"/>
              <p className="text-sm mt-2 text-[var(--ff-text-secondary)]">
                <span className="font-medium text-[var(--ff-text-primary)]">Click to upload</span> or drag a photo here
              </p>
              <p className="text-[11px] text-[var(--ff-text-muted)] mt-1">JPG / PNG / WEBP up to 5 MB</p>
            </>
          )}
        </div>
      )}
      {err && <p className="text-xs text-rose-700 mt-2 flex items-center gap-1" data-testid={`${testId}-error`}><ImageOff size={12}/> {err}</p>}
      {!value && !busy && (
        <div className="mt-2">
          <button type="button" onClick={() => setShowUrlInput(s => !s)} className="text-[11px] text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] underline flex items-center gap-1" data-testid={`${testId}-toggle-url`}>
            <LinkIcon size={11}/> {showUrlInput ? "Hide" : "Or paste image URL"}
          </button>
          {showUrlInput && (
            <div className="flex gap-2 mt-2">
              <input
                type="url"
                placeholder="https://…"
                className="input-base flex-1 text-sm"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                data-testid={`${testId}-url-input`}
              />
              <button type="button" className="btn-secondary text-sm" disabled={!urlInput.trim()} onClick={() => { onChange(urlInput.trim()); setUrlInput(""); setShowUrlInput(false); }} data-testid={`${testId}-url-apply`}>Use</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
